import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import AuthLayout from '@/components/AuthLayout';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: form.password });
      toast.success('Password updated! Please log in with your new password.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout heading="Invalid link" subtitle="This password reset link is not valid">
        <div className="p-8 text-center space-y-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #000' }}>
          <AlertCircle size={36} strokeWidth={1.5} className="mx-auto" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            The reset link is missing or invalid. Please request a new one.
          </p>
          <Link to="/forgot-password" style={{ color: '#000', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
            Request a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout heading="Set new password" subtitle="Choose a strong password for your account">
      <div className="p-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #000' }}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="password" className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              New password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPass ? 'text' : 'password'}
                required
                placeholder="At least 6 characters"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="h-11 rounded-none"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA', paddingRight: '2.75rem' }}
                data-testid="reset-password-new"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70" style={{ color: 'var(--text-muted)' }}>
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              Confirm new password
            </Label>
            <Input
              id="confirm"
              type="password"
              required
              placeholder="Repeat your password"
              value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              className="h-11 rounded-none"
              style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA' }}
              data-testid="reset-password-confirm"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-12 flex items-center justify-center gap-2 mt-2 uppercase tracking-wider font-semibold"
            disabled={loading}
            data-testid="reset-password-submit"
            style={{ backgroundColor: '#000', color: 'white', borderRadius: 0, fontSize: '0.8rem' }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; } }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Updating…
              </span>
            ) : (<><span>Update password</span><ArrowRight size={15} /></>)}
          </Button>
        </form>
      </div>
      <div className="mt-6 text-center">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Remembered it?{' '}
          <Link to="/login" style={{ color: '#000', fontWeight: 600 }}>Back to login</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
